import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import ImpactStrip from "../components/ImpactStrip";
import CurrentRole from "../components/CurrentRole";
import Capabilities from "../components/Capabilities";
import FeaturedCaseStudy from "../components/FeaturedCaseStudy";
import BuiltToRun from "../components/BuiltToRun";
import InTheField from "../components/InTheField";
import FinalCTA from "../components/FinalCTA";
import { BRAND } from "../data/content";
import { useJsonLd, PERSON_SCHEMA } from "../hooks/useJsonLd";

/**
 * Homepage — 2026-09 editorial redesign.
 *
 * Section order per approved brief:
 *   1  Hero              — portrait right, "Built to run without me." signature overlay
 *   2  ImpactStrip       — full-width horizontal evidence line
 *   3  CurrentRole       — ASU editorial current-chapter section
 *   4  Capabilities      — Systems · Impact · People on PAPER background
 *   5  FeaturedCaseStudy — "Selected work" (existing case study feed)
 *   6  BuiltToRun        — Philosophy section (grown from a small block into a large one)
 *   7  InTheField        — Programs in the wild — real photography
 *   8  FinalCTA          — Let's connect
 *
 * All other previous sections (About teaser, AudienceRouter, Diagnostic promo,
 * Testimonials teaser, old closing CTA) are removed. Every dropped section
 * still lives on its own page — the home is now purely editorial and drives
 * visitors into those pages via the CTAs above.
 */
export default function Home() {
  useJsonLd("person-home", PERSON_SCHEMA);
  return (
    <main id="main" data-testid="home-page">
      <Hero />
      <ImpactStrip />
      <CurrentRole />
      <Capabilities />
      <div id="impact"><FeaturedCaseStudy /></div>
      <BuiltToRun />
      <InTheField />
      <FinalCTA />
    </main>
  );
}

/**
 * Editorial hero — ~48/52 split. Left is oversized typography, right is the
 * portrait integrated into the dark background (no rectangular frame) with a
 * gold handwritten "Built to run without me." annotation overlaid.
 */
function Hero() {
  return (
    <section
      className="relative bg-[#0B0B0C] text-[#FAF9F6] overflow-hidden grain"
      data-testid="hero"
    >
      {/* Subtle warm radial glow — same visual grammar carries through the page */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 500px at 92% 8%, rgba(216,169,54,0.12), transparent 60%), radial-gradient(700px 400px at 0% 100%, rgba(216,169,54,0.05), transparent 60%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-10 lg:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* LEFT — editorial copy (~48-50%) */}
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow"
              data-testid="hero-name"
            >
              JJ Argent
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-4 font-display leading-[0.98] tracking-[-0.03em]"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4.75rem)" }}
            >
              Systems builder. <br />
              Problem solver. <br />
              <span className="text-[#D8A936]">People enabler.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-5 font-display text-lg md:text-xl text-[#FAF9F6] leading-snug max-w-xl"
              data-testid="hero-role-line"
            >
              Senior Operations, Programs &amp; Digital Transformation Leader
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-4 max-w-xl text-[15px] md:text-[15.5px] leading-[1.55] text-[#A9A9A6]"
            >
              {BRAND.support}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] tracking-[0.22em] uppercase font-display"
              data-testid="hero-evidence-row"
            >
              <span className="text-[#D8A936]">14+ Years Experience</span>
              <span className="w-6 h-px bg-[#343437]" aria-hidden />
              <span className="text-[#FAF9F6]/80">People · Systems · Impact</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-6 flex flex-row flex-wrap gap-3 items-center"
            >
              <Link
                to="/case-studies"
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D8A936] text-[#0B0B0C] font-display text-[11px] tracking-[0.18em] uppercase border border-[#D8A936] hover:bg-transparent hover:text-[#D8A936] transition-colors"
              >
                Explore my work <ArrowUpRight size={13} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent text-[#FAF9F6] font-display text-[11px] tracking-[0.18em] uppercase border border-[#343437] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
              >
                View experience <ArrowUpRight size={13} />
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-5 text-[13px] text-[#A9A9A6]"
            >
              {BRAND.location} · Open to senior roles, projects and partnerships.
            </motion.p>
          </div>

          {/* RIGHT — portrait integrated into the dark background */}
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="relative aspect-[4/5] max-w-sm mx-auto lg:max-w-none max-h-[62vh] overflow-hidden">
                <picture>
                  <source
                    type="image/webp"
                    srcSet={BRAND.portraitSrcSet}
                    sizes="(min-width: 1024px) 42vw, 92vw"
                  />
                  <img
                    src={BRAND.portrait}
                    srcSet={BRAND.portraitSrcSetJpg}
                    sizes="(min-width: 1024px) 42vw, 92vw"
                    alt="JJ Argent — Senior Operations, Programs & Digital Transformation Leader"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="hero-portrait"
                  />
                </picture>
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(11,11,12,0.95) 0%, rgba(11,11,12,0) 38%), linear-gradient(to bottom, rgba(11,11,12,0.55) 0%, rgba(11,11,12,0) 32%), linear-gradient(to left, rgba(11,11,12,0.6) 0%, rgba(11,11,12,0) 30%), linear-gradient(to right, rgba(11,11,12,0.7) 0%, rgba(11,11,12,0) 28%)",
                  }}
                  aria-hidden
                />
                <div className="absolute left-0 right-0 bottom-3 px-5">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                    Portrait · JJ Argent · Melbourne
                  </p>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: -6, rotate: -6 }}
                animate={{ opacity: 1, y: 0, rotate: -6 }}
                transition={{ duration: 1.1, delay: 1.2 }}
                aria-hidden
                className="absolute -top-2 -right-1 lg:-top-4 lg:-left-8 lg:right-auto max-w-[200px] lg:max-w-[220px] pointer-events-none"
              >
                <span
                  className="font-signature text-3xl md:text-4xl lg:text-[40px] text-[#D8A936] leading-[0.9] drop-shadow-[0_2px_16px_rgba(216,169,54,0.35)] inline-block"
                  style={{ letterSpacing: "-0.005em" }}
                >
                  Built to run <br />
                  without me.
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Fine gold rule connecting hero directly to evidence strip */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="h-px bg-gradient-to-r from-transparent via-[#D8A936]/40 to-transparent" aria-hidden />
      </div>
    </section>
  );
}
